import React, { useEffect, useRef, useState } from 'react'
import QuizContent from '../shared/QuizContent'
import GoogleIcon from '../../assets/google'
import { createPreLanderAccount, getGoogleAuthUrl, createModelParams, convertSelectionsToApiFormat, collectAndStoreUTMData, getExternalClickIds,submitEmailOnly, LP_SOURCE } from '../../utils/api' // Use main API service
import { useQuizSelections } from '../../hooks/useLocalStorage'
import TermsOfService from './termsOfService'
import PrivacyPolicy from './privacyPolicy'
import { useFormValidation, validationRules } from '../../hooks/useFormValidation'
import { buildPaywallUrl } from '../../utils/redtrack'
import { useTranslation } from 'react-i18next'
import { useGBValue } from '../../hooks/useGBValue'
import { trackRegistrationStarted, trackRegistrationCompleted } from '../../utils/analytics'
import OtpStep from './otpStep'

const Step14 = ({ onContinue }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [otpState, setOtpState] = useState(null)
  const [selections] = useQuizSelections() // Get quiz selections
  const googleBtnRef = useRef(null)

  // Use the custom validation hook
  const { values, validateAll, getFieldProps } = useFormValidation(
    { email: '', password: '' },
    {
      email: validationRules.email,
      password: validationRules.password,
    },
  )

  const { t } = useTranslation()
  const ab_testing = useGBValue()
  const isMagicLink = ab_testing['createsexfriends-gray-magicLink'] === 'ON'

  useEffect(() => {
    const utmObj = collectAndStoreUTMData()
    let cancelled = false
    ;(async () => {
      const externalClickIds = await getExternalClickIds()
      if (cancelled || !googleBtnRef.current) return
      const finalState = {
        ...utmObj,
        lp_source: LP_SOURCE,
        externalClickIds,
      }
      const googleAuthUrl = getGoogleAuthUrl()
      googleBtnRef.current.href = `${googleAuthUrl}?state=${encodeURIComponent(JSON.stringify(finalState))}`
      googleBtnRef.current.onclick = () => {
        trackRegistrationStarted('google')
        localStorage.setItem('googleAuthInProgress', 'true')
        localStorage.setItem('googleAuthStartTime', Date.now().toString())
        localStorage.setItem('pendingRegistrationSource', 'google')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const pendingSource = localStorage.getItem('pendingRegistrationSource')
    const isCompleted = localStorage.getItem('isCompleted')

    if (pendingSource === 'google' && isCompleted) {
      trackRegistrationCompleted('google')
      localStorage.removeItem('pendingRegistrationSource')
    }
  }, [])

  const handleEmailOnlySubmit = async () => {
    const emailErr = validationRules.email(values.email)
    if (emailErr) {
      setEmailError(emailErr)
      return
    }
    setEmailError('')
    setIsLoading(true)
    setError('')
    try {
      const data = await submitEmailOnly(values.email, { selections, ab_testing })

      if (data?.success === false) {
        setEmailError(data.message || 'Email already used')
        return
      }

      if (data?.otp_sent) {
        setOtpState({ email: values.email, otpExpiry: data.otpExpiry })
        return
      }

      if (data?.redirectUrl || data?.loginUrl) {
        trackRegistrationCompleted('manual_otp')
        localStorage.setItem('isCompleted', true)
        window.location.href = data.redirectUrl || data.loginUrl
        return
      }

      setError(data?.message || 'Something went wrong')
    } catch (err) {
      setError(err?.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (isMagicLink) {
      await handleEmailOnlySubmit()
      return
    }

    // Validate all fields
    if (!validateAll()) {
      return
    }

    setIsLoading(true)
    setError('')
    trackRegistrationStarted('manual')

    try {
      if (selections) {
        const userAnswers = await convertSelectionsToApiFormat(selections)
        console.log('✅ Всі відповіді користувача з ID:', userAnswers)

        Object.entries(userAnswers.attributes).forEach(([key, value]) => {
          console.log(`${key}: ${value}`)
        })
      }

      // Create account with selections for model-params
      const data = await createPreLanderAccount(values.email, values.password, selections)

      if (data.redirectUrl) {
        window.location.href = data.redirectUrl
        return
      }

      if (data.success) {
        trackRegistrationCompleted('manual', values.email)
        const modelParams = selections ? await createModelParams(selections) : null
        const isPw = window.location.pathname.includes('/pw')
        const paywallUrl = buildPaywallUrl(data.authToken, 'login_paywall', modelParams, selections?.welcome || 'Realistic', isPw && '2')

        localStorage.setItem('isCompleted', true)
        window.location.href = paywallUrl
      } else {
        setEmailError(data.message || 'Email already used')
      }
    } catch (err) {
      setError(err.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptPrivacy = () => {
    setShowPrivacy(false)
  }

  const handleAcceptTerms = () => {
    setShowTerms(false)
  }

  // Get CSS classes for input styling
  const getEmailInputClass = () => {
    const emailProps = getFieldProps('email')
    if (emailProps.hasError) return 'form-input error-input'
    return 'form-input'
  }

  const getPasswordInputClass = () => {
    const passwordProps = getFieldProps('password')
    if (passwordProps.hasError) return 'form-input error-input'
    return 'form-input'
  }

  const handleSignIn = () => {
    if (onContinue) {
      onContinue()
    }
  }

  const handleOtpVerified = data => {
    trackRegistrationCompleted('manual_otp')
    localStorage.setItem('isCompleted', true)
    if (data?.redirectUrl || data?.loginUrl) {
      window.location.href = data.redirectUrl || data.loginUrl
    }
  }

  if (otpState) {
    return (
      <QuizContent className='account-page'>
        <OtpStep
          email={otpState.email}
          selections={selections}
          ab_testing={ab_testing}
          initialOtpExpiry={otpState.otpExpiry}
          onBack={() => setOtpState(null)}
          onVerified={handleOtpVerified}
        />
      </QuizContent>
    )
  }

  return (
    <div>
      <QuizContent className={`account-page${isMagicLink ? ' account-page--magic' : ''}`}>
        <div className='account-container'>
          <div className='account-form-wrapper'>
            <div className='form-header'>
              <h2 className='account-title'>{t('create_account')}</h2>
              {<p className='account-subtitle'>{t('she_waiting')}</p>}
              <button className='close-button'>×</button>
            </div>

            <form onSubmit={handleSubmit} className='account-form'>
              <a ref={googleBtnRef} href='#' className='google-button'>
                <GoogleIcon />
                {isMagicLink ? 'Google' : t('google')}
              </a>

              <div className='separator'>
                <span>{t('or')}</span>
              </div>

              <div className='input-wrapper'>
                <input
                  placeholder='Email'
                  type='email'
                  value={getFieldProps('email').value}
                  onChange={getFieldProps('email').onChange}
                  onBlur={getFieldProps('email').onBlur}
                  className={getEmailInputClass()}
                  required
                  aria-invalid={getFieldProps('email').hasError}
                  aria-describedby={getFieldProps('email').hasError ? 'create-email-error' : undefined}
                  autoComplete='email'
                />
              </div>
              {(getFieldProps('email').hasError || emailError) && (
                <div id='create-email-error' className='error-message-input'>
                  {getFieldProps('email').error || emailError}
                </div>
              )}
              {!isMagicLink && (
                <div className='cardStep15InputWrap'>
                  <div className='input-wrapper'>
                    <input
                      placeholder='Password'
                      type={showPassword ? 'text' : 'password'}
                      value={getFieldProps('password').value}
                      onChange={getFieldProps('password').onChange}
                      onBlur={getFieldProps('password').onBlur}
                      className={getPasswordInputClass()}
                      required
                      minLength={6}
                      aria-invalid={getFieldProps('password').hasError}
                      aria-describedby={getFieldProps('password').hasError ? 'create-password-error' : undefined}
                      autoComplete='new-password'
                    />
                    <button
                      type='button'
                      className='cardStep15InputSvg'
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                          <path
                            d='M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z'
                            stroke='#9B9CAA'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                          <path
                            d='M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z'
                            stroke='#9B9CAA'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      ) : (
                        <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                          <path
                            d='M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421'
                            stroke='#9B9CAA'
                            strokeWidth='1.5'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  {getFieldProps('password').hasError && (
                    <div id='create-password-error' className='error-message-input'>
                      {getFieldProps('password').error}
                    </div>
                  )}
                </div>
              )}
              {error && <div className='error-message'>{error}</div>}

              <button type='submit' className='submit-button' disabled={isLoading}>
                {isLoading ? t('creating_account') : t('create_account2')}
              </button>

              <div className='terms-text'>
                {t('by_signing_up')}{' '}
                <span onClick={() => setShowTerms(true)} className='terms-link'>
                  Terms of Service &{/* {t('terms_of')} */}
                </span>{' '}
                <span onClick={() => setShowPrivacy(true)} className='terms-link'>
                  {/* {t('privacy_policy')} */}
                  Privacy Policy
                </span>
              </div>

              {!isMagicLink && (
                <div className='login-link'>
                  {t('already_have')}{' '}
                  <button type='button' onClick={handleSignIn} className='link-button'>
                    {t('log_in')} &gt;
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </QuizContent>
      {showPrivacy && <PrivacyPolicy isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} onAccept={handleAcceptPrivacy} />}
      {showTerms && <TermsOfService isOpen={showTerms} onClose={() => setShowTerms(false)} onAccept={handleAcceptTerms} />}
    </div>
  )
}

export default Step14
